<template>
  <div class="row">
    <loading
      class="custom-loading-overlay"
      :active="isLoading"
      :color="loadingColor"
      :isFullPage="true"
    ></loading>

    <section v-if="page == 1">
      <div class="col-12" style="padding: 0">
        <div class="page-header">
          <h1 class="page-title">
            Nº da Ocorrência #{{
              atendimento.ocorrencia_id_samu
                ? String(atendimento.ocorrencia_id_samu).slice(-4)
                : atendimento.ocorrencia_id
            }}
          </h1>
          <section v-if="atendimento.status <= 9">
            <div class="status-indicator">
              <span
                :class="
                  atendimento.status === 1 || atendimento.status === 0
                    ? 'status-dot'
                    : 'status-dot-2'
                "
              ></span>
              Atendimento registrado
            </div>
            <div
              v-if="atendimento.status >= 3"
              class="status-indicator"
              :class="'status-' + getStatusClass(atendimento.status)"
            >
              <span
                :class="
                  atendimento.status === 3 ? 'status-dot' : 'status-dot-2'
                "
              ></span>
              Ambulância Solicitada
            </div>
            <div
              v-if="atendimento.status >= 4"
              class="status-indicator"
              :class="'status-' + getStatusClass(atendimento.status)"
            >
              <span
                :class="
                  atendimento.status === 4 ? 'status-dot' : 'status-dot-2'
                "
              ></span>
              Ambulância próxima do local da ocorrência
            </div>
            <div
              v-if="atendimento.status >= 5"
              class="status-indicator"
              :class="'status-' + getStatusClass(atendimento.status)"
            >
              <span
                :class="
                  atendimento.status === 5 ? 'status-dot' : 'status-dot-2'
                "
              ></span>
              Ambulância chegou
            </div>
            <div
              v-if="atendimento.status == 9"
              class="status-indicator"
              :class="'status-' + getStatusClass(atendimento.status)"
            >
              <span
                :class="
                  atendimento.status === 9 ? 'status-dot' : 'status-dot-2'
                "
              ></span>
              Finalizado
            </div>
          </section>
          <div
            v-if="atendimento.status == 99"
            class="status-indicator"
            :class="'status-' + getStatusClass(atendimento.status)"
          >
            <span
              :class="
                atendimento.status === 99
                  ? 'status-cancelado'
                  : 'status-cancelado'
              "
            ></span>
            Cancelado
          </div>
        </div>

        <div class="content-container">
          <div class="section" v-if="shouldShowMap && atendimento.status < 9">
            <h2 class="section-title">
              <i class="bi bi-car-front-fill"></i> Previsão de Chegada:
              {{ atendimento.hora_chegada_f }}
            </h2>
            <div class="map-container">
              <GmapMap
                ref="gmap"
                :center="{ lat: position.lat_v, lng: position.lng_v }"
                :options="{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false
                }"
                :zoom="15"
                map-type-id="roadmap"
                style="width: 100%; height: 300px; border-radius: 15px"
              >
                <GmapMarker
                  :key="`${position.lat_v}-${position.lng_v}`"
                  :icon="{
                    url: require('@/assets/images/pin_ambulancia_rd.png'),
                    scaledSize: { width: 35, height: 35 }
                  }"
                  :position="{ lat: position.lat_v, lng: position.lng_v }"
                />
                <GmapMarker
                  :key="`${position.lat_v}-${position.lng_v}`"
                  :position="{ lat: position.lat, lng: position.lng }"
                  :icon="{
                    url: require('@/assets/images/icons8-marker-30.png'),
                    scaledSize: { width: 25, height: 25 }
                  }"
                >
                  <GmapInfoWindow
                    :options="{
                      pixelOffset: { width: 0, height: -5 },
                      disableAutoPan: true
                    }"
                    :position="{ lat: position.lat, lng: position.lng }"
                  >
                    <div
                      style="background: white; padding: 4px 8px; border-radius: 6px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2); color: black; font-size: 12px; font-weight: bold; margin: 0;"
                    >
                      Chegada {{ atendimento.tempo_estimado }} min
                    </div>
                  </GmapInfoWindow>
                </GmapMarker>
                <GmapPolyline
                  v-if="routeCoords.length"
                  :path="routeCoords"
                  :options="{
                    strokeColor: '#000',
                    strokeOpacity: 1.0,
                    strokeWeight: 3
                  }"
                />
              </GmapMap>
            </div>
          </div>
          <!-- INFORMAÇÕES DO SOLICITANTE -->
          <div class="section">
            <h2 class="section-title">
              <i class="bi bi-person-fill"></i> Informações do Solicitante
            </h2>
            <div class="info-grid two-columns">
              <div class="info-card">
                <span class="info-label">Solicitante</span>
                <span class="info-value">{{
                  atendimento.solicitante
                    ? atendimento.solicitante.usuario_nome
                    : "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Data da Solicitação</span>
                <span class="info-value">{{
                  formatDate(atendimento.data_gravacao)
                }}</span>
              </div>
            </div>
          </div>

          <!-- INFORMAÇÕES DA VÍTIMA -->
          <div class="section">
            <h2 class="section-title">
              <i class="bi bi-heart-pulse"></i> Informações da Vítima
            </h2>
            <div class="info-grid">
              <div class="info-card full-width">
                <span class="info-label">Nome/Aparência</span>
                <span class="info-value">{{
                  atendimento.nome_vitima || "Não informado"
                }}</span>
              </div>
            </div>
            <div class="info-grid three-columns">
              <div class="info-card">
                <span class="info-label">Idade</span>
                <span class="info-value">{{
                  atendimento.idade_vitima || "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Sexo</span>
                <span class="info-value">{{
                  getSexoText(atendimento.sexo_vitima)
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Telefone</span>
                <span class="info-value">{{
                  atendimento.telefone || "Não informado"
                }}</span>
              </div>
            </div>
          </div>

          <!-- QUEIXA PRINCIPAL -->
          <div class="section">
            <h2 class="section-title">
              <i class="bi bi-chat-dots"></i> Queixa Principal
            </h2>
            <div class="queixa-card">
              <p>{{ atendimento.queixa_principal || "Não informado" }}</p>
            </div>
          </div>

          <!-- ENDEREÇO -->
          <div class="section">
            <h2 class="section-title">
              <i class="bi bi-geo-alt-fill"></i> Localização
            </h2>
            <div class="info-grid three-columns">
              <div class="info-card full-width">
                <span class="info-label">Logradouro</span>
                <span class="info-value">{{
                  atendimento.logradouro || "Não informado"
                }}</span>
              </div>
            </div>
            <div class="info-grid three-columns">
              <div class="info-card">
                <span class="info-label">Número</span>
                <span class="info-value">{{
                  atendimento.numero || "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Bairro</span>
                <span class="info-value">{{
                  atendimento.bairro || "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">CEP</span>
                <span class="info-value">{{
                  atendimento.cep || "Não informado"
                }}</span>
              </div>
            </div>
            <div class="info-grid three-columns">
              <div class="info-card">
                <span class="info-label">Cidade</span>
                <span class="info-value">{{
                  atendimento.cidade_data || "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Estado</span>
                <span class="info-value">{{
                  atendimento.uf_data || "Não informado"
                }}</span>
              </div>
              <div class="info-card">
                <span class="info-label">Complemento</span>
                <span class="info-value">{{
                  atendimento.complemento || "-"
                }}</span>
              </div>
            </div>
          </div>

          <!-- MAPA -->

          <div class="section" v-if="!shouldShowMap">
            <h2 class="section-title">
              <i class="bi bi-pin-map"></i> Sua Localização
            </h2>
            <div class="map-container">
              <GmapMap
                :center="{ lat: position.lat, lng: position.lng }"
                :options="{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false
                }"
                :zoom="16"
                map-type-id="roadmap"
                style="width: 100%; height: 250px; border-radius: 15px"
              >
                <GmapMarker :position="position" />
              </GmapMap>
            </div>
          </div>

          <!-- BOTÕES -->
          <div
            class="button-group"
            v-if="atendimento.status != 9 && atendimento.status != 99"
          >
            <button
              class="btn btn-primary"
              @click="ligar192()"
              v-if="atendimento.status == 0"
            >
              <i class="bi bi-telephone-fill"></i> Ligar para o SAMU ({{
                ocorrencia_central
              }})
            </button>
            <button class="btn btn-secondary" @click="openModal()">
              <i class="bi bi-x-circle"></i> Cancelar Atendimento
            </button>
          </div>

          <div style="height: 40px"></div>
        </div>
      </div>

      <!-- Modal Cancelar -->
      <b-modal
        v-model="showModal"
        id="modalRejeitar"
        title="Cancelar Ocorrência"
        centered
        hide-footer
        class="modal-styled"
      >
        <div class="modal-content">
          <label class="modal-label">Justificativa</label>
          <select
            class="form-control"
            v-model="justificativa_selecionada"
            v-if="atendimento.tipo_pessoa && atendimento.tipo_pessoa == '1'"
          >
            <option value="Já estou melhor, não preciso mais de atendimento."
              >Já estou melhor, não preciso mais de atendimento.</option
            >
            <option value="Preferi não aguardar o atendimento."
              >Preferi não aguardar o atendimento.</option
            >
            <option value="Consegui ajuda no local"
              >Consegui ajuda no local</option
            >
            <option value="Outros">Outros</option>
          </select>
          <select
            class="form-control"
            v-model="justificativa_selecionada"
            v-if="atendimento.tipo_pessoa && atendimento.tipo_pessoa == '2'"
          >
            <option value="A vítima já está melhor."
              >A vítima já está melhor.</option
            >
            <option value="A pessoa vai se deslocar por meios próprios."
              >A pessoa vai se deslocar por meios próprios.</option
            >
            <option value="Recusou atendimento.">Recusou atendimento.</option>
            <option value="Outros">Outros</option>
          </select>
          <textarea
            class="modal-textarea mt-2"
            v-model="cancelamento_justificativa"
            placeholder="Informe o motivo do cancelamento..."
            v-if="
              justificativa_selecionada == 'Outros' || !atendimento.tipo_pessoa
            "
          ></textarea>

          <div class="modal-buttons">
            <button class="btn btn-primary" @click="cancelar">
              <i class="bi bi-check-circle"></i> Salvar
            </button>
            <button class="btn btn-secondary" @click="closeModal">
              <i class="bi bi-x-circle"></i> Fechar
            </button>
          </div>
        </div>
      </b-modal>
    </section>
    <section v-if="page == 2">
      <div
        class="card card-style"
        style="background-color: white; margin-left: 0px; margin-right: 0px"
      >
        <div class="card-body">
          <div class="chat">
            <div v-if="atendimento">
              <div class="chat-header clearfix">
                <div class="row">
                  <div class="col-2">
                    <a
                      href="javascript:void(0);"
                      data-toggle="modal"
                      data-target="#view_info"
                    >
                      <img :src="atendimento.solicitante.foto" alt="avatar" />
                    </a>
                  </div>
                  <div class="col-9">
                    <div class="chat-about">
                      <h6 class="m-b-0">Operador SAMU</h6>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="chat-history"
                id="chat-messages"
                style="overflow: auto; max-height: 350px"
              >
                <ul class="m-b-0">
                  <li
                    class="clearfix"
                    v-for="message in messages"
                    :key="message.mensagem_id"
                  >
                    <div
                      class="message-data"
                      :class="
                        user.usuario_id == message.usuario_id
                          ? 'text-right'
                          : 'text-left'
                      "
                    >
                      <span class="message-data-time">{{
                        moment(message.enviado_em).fromNow()
                      }}</span>
                    </div>
                    <div
                      class="message"
                      :class="
                        user.usuario_id == message.usuario_id
                          ? 'other-message float-right'
                          : 'my-message float-left'
                      "
                    >
                      {{ message.mensagem }}
                    </div>
                  </li>
                </ul>
              </div>
              <div class="chat-message clearfix">
                <div class="input-group mb-0">
                  <div
                    class="input-group-prepend"
                    style="cursor: pointer"
                    @click="sendMessage"
                    v-if="atendimento.status != 9"
                  >
                    <span class="input-group-text"
                      ><i class="fas fa-paper-plane"></i
                    ></span>
                  </div>
                  <input
                    :disabled="atendimento.status === 9 || sending_message"
                    type="text"
                    class="form-control"
                    placeholder="Mensagem..."
                    v-model="message"
                    v-on:keyup.enter="sendMessage"
                    id="message_area"
                  />
                </div>
              </div>
              <div v-if="atendimento.status === 9" class="finalized-message">
                O atendimento está finalizado. Não é possível enviar mais
                mensagens.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import vSelect from "vue-select";
import moment from "moment";
/* import GmapMap from 'vue2-google-maps'; */
export default {
  data() {
    return {
      justificativa_selecionada: null,
      cancelamento_justificativa: null,
      showModal: false,
      routeCoords: [],
      page: 1,
      sending_message: false,
      moment: moment,
      loaded: false,
      messages: [],
      message: "",
      user: {},
      position: {
        lat: 0,
        lng: 0
      },
      atendimento: {
        status: 0,
        data_gravacao: "",
        paciente: "",
        solicitante: {
          usuario_nome: "",
          nome_paciente: "",
          idade: "",
          sexo: "",
          queixa_principal: ""
        },
        ocorrencia_id: "",
        latitude: 0,
        longitude: 0
      },
      isDisabled: true,
      isLoading: false,
      loadingColor: "#5D9CEC",
      ocorrencia_central: [],
      shouldShowMap: false,
      ligouParaSamu: false,
      markerIcon: {
        url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png", // Ícone para o marcador
        scaledSize: { width: 40, height: 40 } // Tamanho do marcador
      }
    };
  },
  computed: {},
  methods: {
    async getRouteFromORS(origem, destino) {
      const body = {
        coordinates: [
          [origem.lng, origem.lat],
          [destino.lng, destino.lat]
        ]
      };

      try {
        const res = await axios.post(
          this.$store.state.api + "get-route", // Rota no seu Laravel
          body
        );

        const coords = res.data.features[0].geometry.coordinates;
        this.routeCoords = coords.map(([lng, lat]) => ({ lat, lng }));
        console.log("Coords recebidas:", this.routeCoords);
        this.$nextTick(() => {
          this.fitMapToBounds();
        });
      } catch (error) {
        console.error("Erro ao buscar rota via backend:", error);
      }
    },
    async fitMapToBounds() {
      if (!this.routeCoords.length || !this.$refs.gmap) return;
      const mapObject = await this.$refs.gmap.$mapPromise;
      const bounds = new window.google.maps.LatLngBounds();
      this.routeCoords.forEach(coord => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
      });
      // incluir também a posição da vítima
      if (this.position.lat && this.position.lng) {
        bounds.extend(
          new window.google.maps.LatLng(this.position.lat, this.position.lng)
        );
      }
      mapObject.fitBounds(bounds);
    },
    ligar192() {
      let telefone = this.ocorrencia_central;

      if (telefone) {
        telefone = String(telefone).replace(/^https?:\/\//, "");

        window.location.href = `tel:${telefone}`;
        this.ligouParaSamu = true;
      } else {
        console.error("Número de telefone não encontrado.");
      }
    },
    formatDate(date) {
      if (!date) return "Não informado";
      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return "Não informado";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} às ${hours}:${minutes}`;
      } catch (error) {
        return "Não informado";
      }
    },
    getSexoText(sexo) {
      if (sexo === "M") return "Masculino";
      if (sexo === "F") return "Feminino";
      return "Não informado";
    },
    getStatusClass(status) {
      const statusMap = {
        0: "solicitado",
        1: "andamento",
        2: "finalizado",
        9: "cancelado"
      };
      return statusMap[status] || "solicitado";
    },
    closeModal() {
      this.showModal = false;
    },
    openModal() {
      this.showModal = true;
      this.$bvModal.show("modalRejeitar");
    },
    cancelar() {
      const self = this;

      if (self.justificativa_selecionada != "Outros") {
        self.cancelamento_justificativa = self.justificativa_selecionada;
      }

      self.atendimento.justificativa_cancelamento =
        self.cancelamento_justificativa;

      if (
        !self.atendimento.justificativa_cancelamento ||
        self.atendimento.justificativa_cancelamento.trim() === ""
      ) {
        self.$message(null, "O campo Justificativa é obrigatório!", "error");
        return;
      }

      let api = self.$store.state.api + "solicitacoes/cancelarOcorrencia";

      swal({
        title: "Deseja cancelar esta ocorrência?",
        text: "Essa operação não pode ser desfeita",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: "Sim, cancelar!",
        showLoaderOnConfirm: true,
        buttons: {
          catch: {
            text: "Não",
            value: "cancel",
            className: "btn-danger"
          },
          confirm: {
            text: "Sim",
            value: "confirm"
          }
        }
      }).then(value => {
        switch (value) {
          case "cancel":
            swal("Cancelado", "A ocorrência não foi cancelada!", "info");
            break;

          case "confirm":
            axios
              .post(api, {
                ocorrencia_id: self.atendimento.ocorrencia_id,
                justificativa: self.atendimento.justificativa_cancelamento
              })
              .then(response => {
                swal("Sucesso!", "A ocorrência foi cancelada!", "success");
                self.$router.push("/dashboard");
              })
              .catch(error => {
                self.$message(null, error.response.data, "error");
              });
            break;
        }
      });
    },
    rolar() {
      setTimeout(() => {
        $("#chat-messages").scrollTop($("#chat-messages")[0].scrollHeight);
      }, 1000);
    },
    sendMessage: function() {
      const self = this;
      const api = self.$store.state.api + "chat/send-message";

      self.sending_message = true;

      axios
        .post(api, {
          mensagem: self.message,
          ocorrencia_id: self.$route.params.id
        })
        .then(response => {
          self.message = "";
          self.sending_message = false;
          self.getMessages();
          setTimeout(() => {
            $("#message_area").focus();
          }, 500);
        })
        .catch(error => {
          self.$message(null, error.response.data, "error");
          self.sending_message = false;
        });
    },
    readMessages: function() {
      const self = this;
      const api = self.$store.state.api + "chat/read-messages";

      if (self.$route.params.id) {
        axios
          .post(api, {
            ocorrencia_id: self.$route.params.id
          })
          .then(response => {})
          .catch(error => {
            self.$message(null, error.response.data, "error");
          });
      }
    },
    playSound(volume) {
      var audio = new Audio(
        "https://soundbible.com/mp3/Air%20Plane%20Ding-SoundBible.com-496729130.mp3"
      );
      if (volume == null || volume == undefined) {
        volume = 0;
      }
      audio.volume = volume;
      audio.play();
    },
    getMessages() {
      const self = this;

      const api =
        self.$store.state.api +
        "chat_messages?paginated=false&with[]=usuario&ocorrencia_id=" +
        self.atendimento.ocorrencia_id;
      axios
        .get(api)
        .then(response => {
          self.messages = response.data.data;
          self.rolar();
          self.readMessages();
          self.loaded = true;
        })
        .catch(error => {
          self.$message(null, error.response.data, "error");
          self.$loading(false);
        });
    },

    getOcorrenciaCentral(id) {
      const self = this;

      const api = self.$store.state.api + "ocorrencias_central/" + id;

      axios
        .get(api)
        .then(response => {
          self.ocorrencia_central = response.data;
          self.loaded = true;
        })
        .catch(error => {
          self.$message(null, error.response.data, "error");
          self.$loading(false);
        });
    },
    getAtendimento(id) {
      const self = this;

      if (self.positionInterval) {
        clearInterval(self.positionInterval);
      }

      self.positionInterval = setInterval(() => {
        const api =
          self.$store.state.api +
          "ocorrencia?with[]=paciente&with[]=solicitante&with[]=status_desc&ocorrencia_id=" +
          id;
        axios
          .get(api)
          .then(response => {
            self.atendimento = response.data.data[0];
            self.position.lat = parseFloat(self.atendimento.latitude);
            self.position.lng = parseFloat(self.atendimento.longitude);
            self.position.lat_v = parseFloat(self.atendimento.latitude_veiculo);
            self.position.lng_v = parseFloat(
              self.atendimento.longitude_veiculo
            );
            if (self.position.lat_v && self.position.lng_v) {
              self.getRouteFromORS(
                { lat: self.position.lat_v, lng: self.position.lng_v },
                { lat: self.position.lat, lng: self.position.lng }
              );
            }
            const agora = moment();
            const horaChegada = moment(self.atendimento.hora_chegada);
            const diffMin = horaChegada.diff(agora, "minutes");

            self.shouldShowMap = diffMin <= 5;

            const id = self.atendimento.ocorrencia_id;

            axios
              .get(`${self.$store.state.api}ocorrencia_posicao/${id}`)
              .then(res => {
                self.position.lat_v = parseFloat(res.data.latitude_veiculo);
                self.position.lng_v = parseFloat(res.data.longitude_veiculo);

                self.$forceUpdate();

                if (
                  self.shouldShowMap &&
                  self.$refs.gmap &&
                  self.$refs.gmap.panTo
                ) {
                  self.$refs.gmap.panTo({
                    lat: self.position.lat_v,
                    lng: self.position.lng_v
                  });
                }
              })
              .catch(err => {
                console.error("Erro ao buscar posição do veículo:", err);
              });

            axios
              .get(`${self.$store.state.api}ocorrencia_hora_chegada/${id}`)
              .then(res => {
                self.atendimento.hora_chegada = res.data.hora_chegada;

                const agora = moment();
                const horaChegada = moment(self.atendimento.hora_chegada);
                const diffMin = horaChegada.diff(agora, "minutes");

                self.atendimento.tempo_estimado = diffMin >= 0 ? diffMin : 0;

                self.shouldShowMap = diffMin <= 5 && diffMin >= 0;

                console.log("shouldShowMap", self.shouldShowMap);
                console.log(
                  "hora_chegada atualizada",
                  self.atendimento.hora_chegada
                );
              })
              .catch(err => {
                console.error("Erro ao buscar hora_chegada:", err);
              });

            self.isLoading = false;

            // self.getMessages();

            setTimeout(() => {
              Echo.private(
                "ocorrencia." + self.atendimento.ocorrencia_id
              ).listen(".Alert", data => {
                var data_message = JSON.parse(data.message);
                console.log(JSON.parse(data.message));
                if (self.user.usuario_id != data_message.usuario_id) {
                  self.messages.push(JSON.parse(data.message));
                }
                self.readMessages();
                self.rolar();
                setTimeout(() => {
                  if (self.user.usuario_id != data_message.usuario_id) {
                    const greeting = new Notification("Samu IT4D", {
                      body: "Nova mensagem no chat!",
                      icon:
                        "http://triagem.ares-saude.com.br/img/logo.083ed3b8.png"
                    });
                    self.playSound(0.5);
                    self.$message("Chat", "Nova mensagem no chat", "info");
                  }
                  $("#chat-messages").scrollTop(
                    $("#chat-messages")[0].scrollHeight
                  );
                }, 100);
              });
            }, 1000);
          })
          .catch(error => {
            self.$message(null, error.response.data, "error");
            self.isLoading = false;
          });
      }, 5000);
    }
  },
  beforeDestroy() {
    const self = this;
    Echo.leave("ocorrencia." + self.atendimento.ocorrencia_id);
    if (this.positionInterval) {
      clearInterval(this.positionInterval);
    }
  },
  beforeDestroy() {
    const self = this;
    Echo.leave("ocorrencia." + self.atendimento.ocorrencia_id);
    if (this.positionInterval) {
      clearInterval(this.positionInterval);
    }
  },
  components: {
    Loading,
    vSelect
  },
  props: {},
  mounted: function() {
    const self = this;
    self.isLoading = true;

    // setTimeout(() => {
    //   window.addEventListener("blur", function() {
    //     self.getMessages();
    //   });

    //   window.addEventListener("focus", function() {
    //     self.getMessages();
    //   });
    // }, 2000);

    setTimeout(() => {
      self.user = self.$store.state.user;
    }, 3000);

    const ocorrenciaId = self.$route.params.id;

    window.Echo.private("atendimento_status." + ocorrenciaId).listen(
      ".AtendimentoStatus",
      e => {
        if (e && e.ocorrencia !== undefined) {
          this.atendimento.status_desc = {
            ...this.atendimento.status_desc,
            nome: e.ocorrencia.status_desc.nome
          };

          console.log("Status atualizado:", e.ocorrencia.status_desc.nome);
        } else {
          console.log("Evento recebido sem dados de status.");
        }
      }
    );

    if (ocorrenciaId) {
      setTimeout(() => {
        self.getAtendimento(ocorrenciaId);
        self.getOcorrenciaCentral(ocorrenciaId);
      }, 1500);
    }

    $("#menu-main").removeClass("show");
    $(".offcanvas-backdrop.show").removeClass("show");
    /* $(".theme-light").removeAttribute("style","overflow: hidden; padding-right: 0px;"); */
    document.body.setAttribute("style", null);

    /* if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition((position) => {

        this.ocorrencia.latitude = position.coords.latitude;
        this.ocorrencia.longitude = position.coords.longitude;

        this.position.lat = position.coords.latitude;
        this.position.lng = position.coords.longitude;

        self.isLoading = false;
    });
    } else {
        console.log("Geolocalização não é suportada pelo seu navegador");
        self.isLoading = false;
    } */
  }
};
</script>
<style scoped>
/* PAGE HEADER */
.page-header {
  margin-bottom: 10px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: rgb(0, 0, 0);
  margin: 0 0 0px 0;
  line-height: 1.2;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgb(0, 0, 0);
  text-transform: capitalize;
  margin-left: 17px;
  margin-bottom: 17px;
}

.status-dot-2 {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #a5a5a5;
  animation: pulse 2s infinite;
  box-shadow: 0 0 10px rgb(92, 92, 92);
}

.status-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #10b981;
  animation: pulse 2s infinite;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.6);
}

.status-cancelado {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #ef4444;
  animation: pulse 2s infinite;
  box-shadow: 0 0 10px rgba(239, 68, 68, 0.6);
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.15);
  }
}

/* CONTENT CONTAINER */
.content-container {
  padding: 0 16px 16px;
  padding-bottom: 160px;
}

/* SECTIONS */
.section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #d6582c;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-title i {
  font-size: 18px;
}

/* INFO GRID */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.info-grid.three-columns {
  grid-template-columns: repeat(3, 1fr);
}

.info-grid.two-columns {
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 768px) {
  .info-grid.three-columns {
    grid-template-columns: repeat(2, 1fr);
  }

  .info-grid.two-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .info-grid,
  .info-grid.three-columns,
  .info-grid.two-columns {
    grid-template-columns: 1fr;
  }
}

.info-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-card:hover {
  border-color: #d6582c;
  box-shadow: 0 4px 12px rgba(214, 88, 44, 0.1);
  transform: translateY(-2px);
}

.info-card.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  word-break: break-word;
}

/* QUEIXA CARD */
.queixa-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fef08a 100%);
  border-left: 4px solid #d6582c;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.queixa-card p {
  margin: 0;
  font-size: 14px;
  color: #78350f;
  line-height: 1.5;
}

/* MAP CONTAINER */
.map-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
}

/* BUTTON GROUP */
.button-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px 16px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent);
  z-index: 999;
}

.btn {
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #1523f1 0%, #1523f1 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(214, 88, 44, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(214, 88, 44, 0.4);
}

.btn-secondary {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
}

/* MODAL STYLING */
.modal-styled ::v-deep .modal-header {
  background: linear-gradient(135deg, #d6582c 0%, #e6744d 100%);
  color: white;
  border: none;
  border-radius: 12px 12px 0 0;
}

.modal-styled ::v-deep .modal-header .close {
  color: white;
  opacity: 0.8;
}

.modal-content {
  padding: 20px;
}

.modal-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s;
}

.modal-textarea:focus {
  outline: none;
  border-color: #d6582c;
  box-shadow: 0 0 0 3px rgba(214, 88, 44, 0.1);
}

.modal-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.modal-buttons .btn {
  margin: 0;
}

/* CHAT STYLING */

/* CUSTOMIZACOES VUE LOADING */
.custom-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0);
  backdrop-filter: blur(3px);
  opacity: 0.98;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.custom-loading-spinner {
  width: 100px;
  height: 100px;
  z-index: 10000;
}
.custom-input {
  border-radius: 0;
}

.input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
}

.input-group > .form-control {
  position: relative;
  flex: 1 1 auto;
  width: 1%;
  margin-bottom: 0;
}

.input-group > .form-control:focus {
  z-index: 3;
}

.custom-input-container {
  position: relative;
}

.custom-input {
  padding: 0.375rem 0.75rem;
  height: 2.714rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  /* box-shadow: 0 0.2rem 0.5rem rgba(0, 0, 0, 0.15); */
  width: 100%;
}

.custom-input:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.vue-map-container .vue-map .gm-style {
  border-radius: 30px !important;
}

.botao-solicitar {
  position: fixed;
  bottom: 0;
  width: 100%;
  padding-left: 10px;
  padding-right: 10px;
  z-index: 999999 !important;
}

.btn-solicitar {
  width: 100% !important;
  background-color: rgb(21, 72, 138) !important;
  margin: 0px 15px 18px -29px;
}

.btn-cancel {
  width: 100% !important;
  background-color: rgb(255, 0, 0) !important;
  margin: 0px 15px 18px -29px;
}

/* CSS do Chat */

.chat {
  font-size: 12px !important;
}

.chat .chat-header {
  padding: 15px 20px !important;
  border-bottom: 2px solid #f4f7f6;
}

.chat .chat-header img {
  float: left !important;
  border-radius: 40px !important;
  width: 40px;
}

.chat .chat-header .chat-about {
  float: left !important;
  padding-left: 10px;
}

.chat .empty {
  min-height: 80vh;
  padding: 10px;
}

.chat .chat-history {
  padding: 20px !important;
  border-bottom: 2px solid #fff;
}

.chat .chat-history ul {
  padding: 0;
}

.chat .chat-history ul li {
  list-style: none !important;
  margin-bottom: 30px;
}

.chat .chat-history ul li:last-child {
  margin-bottom: 0px;
}

.chat .chat-history .message-data {
  margin-bottom: 15px;
}

.chat .chat-history .message-data img {
  border-radius: 40px !important;
  width: 40px;
}

.chat .chat-history .message-data-time {
  color: #434651 !important;
  padding-left: 6px;
}

.chat .chat-history .message {
  color: #444 !important;
  padding: 18px 20px !important;
  line-height: 26px !important;
  font-size: 14px !important;
  border-radius: 7px !important;
  display: inline-block !important;
  position: relative;
}

.chat .chat-history .message:after {
  bottom: 100% !important;
  left: 7% !important;
  border: solid transparent !important;
  content: " " !important;
  height: 0 !important;
  width: 0 !important;
  position: absolute !important;
  pointer-events: none !important;
  border-bottom-color: #fff !important;
  border-width: 10px !important;
  margin-left: -10px;
}

.chat .chat-history .my-message {
  background: #efefef;
  min-width: 15%;
}

.chat .chat-history .my-message:after {
  bottom: 100% !important;
  left: 30px !important;
  border: solid transparent !important;
  content: " " !important;
  height: 0 !important;
  width: 0 !important;
  position: absolute !important;
  pointer-events: none !important;
  border-bottom-color: #efefef !important;
  border-width: 10px !important;
  margin-left: -10px;
}

.chat .chat-history .other-message {
  background: #e8f1f3 !important;
  text-align: right;
  min-width: 15%;
}

.chat .chat-history .other-message:after {
  border-bottom-color: #e8f1f3 !important;
  left: 80% !important;
}

.chat .chat-message {
  padding: 20px;
}

.online,
.offline,
.me {
  margin-right: 2px !important;
  font-size: 8px !important;
  vertical-align: middle;
}

.online {
  color: #86c541;
}

.offline {
  color: #e47297;
}

.me {
  color: #1d8ecd;
}

.float-right {
  float: right;
}

.clearfix:after {
  visibility: hidden !important;
  display: block !important;
  font-size: 0 !important;
  content: " " !important;
  clear: both !important;
  height: 0;
}
.circulo {
  display: inline-flex;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  font-size: 18px;
  animation: animate 2.5s linear infinite;
}
@keyframes animate {
  0% {
    background-color: #fff;
  }

  50% {
    background-color: #25901cd2;
  }

  100% {
    background-color: #fff;
  }
}

.uber-label {
  background: white;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  text-align: center;
  display: inline-block;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/VisualizarAtendimento.vue